import { Input, InputProps } from '@velocity/ui';
import React from 'react';
import FormFieldShell, { FormFieldShellProps } from './form-field-shell';

export interface FormTextFieldProps
  extends
    Omit<
      InputProps,
      | 'error'
      | 'inputRef'
      | 'min'
      | 'max'
      | 'step'
      | 'customSteps'
      | keyof FormFieldShellProps
    >,
    FormFieldShellProps {
  /** Native min / max (typed loosely so `register()` can be spread) */
  min?: number | string;
  max?: number | string;
  /** Overrides the default `input-field-<name>` test id */
  dataTestId?: string;
}

/*
 * Velocity `FormField` + `Input` wired for react-hook-form's `register`.
 * Usage:
 *   <FormTextField label='Vorname(n)' {...register('first_name')} errorMessage={errors.first_name?.message} />
 */
const FormTextField = React.forwardRef<HTMLInputElement, FormTextFieldProps>(
  (
    {
      name,
      label,
      hideLabel,
      errorMessage,
      hint,
      tooltip,
      dataTestId,
      required,
      disabled,
      readOnly,
      min,
      max,
      inputProps,
      className,
      ...inputRest
    },
    ref,
  ) => (
    <FormFieldShell
      {...{
        name,
        label,
        hideLabel,
        errorMessage,
        hint,
        tooltip,
        required,
        disabled,
        readOnly,
        className,
      }}
    >
      <Input
        {...inputRest}
        name={name}
        inputRef={ref}
        inputProps={{
          ...inputProps,
          'aria-label':
            hideLabel && typeof label === 'string' ? label : undefined,
          min,
          max,
          'data-testid': dataTestId ?? `input-field-${name}`,
        }}
      />
    </FormFieldShell>
  ),
);

FormTextField.displayName = 'FormTextField';

export default FormTextField;
